'use client';

import { useEffect } from 'react';
import { useMatches } from 'react-router';
import { CLIENT } from '@/generated/client';

/** Titolo usato quando nessuna rotta abbinata dichiara `handle.title`. */
const FALLBACK_TITLE = CLIENT.appName;

/** Suffisso dopo il separatore: il nome prodotto del client. */
const DEFAULT_SUFFIX: string = CLIENT.appName;

/**
 * Punto mediano fra sezione e brand (scelta del product owner). Resta distinto
 * dal tratto lungo "–"/"—", che INB-103 vieta nel titolo del tab: quello non va
 * reintrodotto qui.
 */
const SEPARATOR = ' · ';

/** Metadati titolo dichiarati da una rotta tramite `handle` in `App.tsx`. */
export type RouteTitleHandle = {
	/** Nome della sezione mostrato nel tab del browser. */
	title?: string;
	/** Sostituisce il nome prodotto per un intero ramo di rotte. */
	titleSuffix?: string;
};

/**
 * Compone il titolo dalle rotte abbinate, ordinate da radice a foglia: l'ultima
 * che dichiara un valore vince, così una figlia sovrascrive il proprio padre.
 * Puro e senza dipendenze da React, per poterlo verificare in isolamento.
 */
export function resolveDocumentTitle(matches: readonly { handle?: unknown }[]): string {
	let title: string | undefined;
	let suffix = DEFAULT_SUFFIX;

	for (const match of matches) {
		const handle = match.handle as RouteTitleHandle | undefined;
		if (handle?.title) title = handle.title;
		if (handle?.titleSuffix) suffix = handle.titleSuffix;
	}

	return title ? `${title}${SEPARATOR}${suffix}` : FALLBACK_TITLE;
}

/**
 * Tiene `document.title` allineato alla rotta corrente (INB-103). Montato una
 * sola volta nel layout radice: legge i `handle` delle rotte abbinate invece di
 * far impostare il titolo a ogni pagina.
 */
export function useDocumentTitle() {
	const matches = useMatches();

	useEffect(() => {
		document.title = resolveDocumentTitle(matches);
	}, [matches]);
}
